/**
 * Светофор — `div.kpi-grid#kpiGrid` (ui-blocks.md §5.1).
 *
 * Состав плиток задаёт бэкенд (`kpi[]`): для главврача 10, для исполнителя 6.
 * Клик по плитке открывает список дел `GET /api/dashboard/list?kpi=<key>`;
 * при нуле список не открывается — тост, как в макете.
 */

import type { KeyboardEvent } from 'react';
import type { KpiTile, OpenList, ShowToast } from './types';
import { Delta } from './Delta';

/** Иконки макета для совпадающих плиток, для новых — по смыслу предиката. */
const KPI_ICONS: Record<string, string> = {
    open: '📂',
    soon: '🔥',
    late: '⏰',
    p1: '⚠️',
    risk: '🛡',
    repeat: '🔁',
    extended: '⏳',
    refund: '💰',
    unassigned: '👤',
    closed: '✅',
    waiting_draft: '📝',
    in_approval: '🖐',
    new_today: '🆕',
};

/** Заголовки модалки со списком — дословно из макета, где плитка совпадает. */
const KPI_TITLES: Record<string, string> = {
    p1: 'Красные флаги: приоритет 1',
    late: 'Просроченные дела',
    risk: 'Обращения с риском проверки',
    soon: 'Срок ответа ≤ 3 дней',
    extended: 'Дела с продлённым сроком',
    refund: 'Денежные требования',
};

/** Тон плитки: цвет числа и цветная полоса слева. */
function toneClass(tile: KpiTile): string {
    const filled = tile.value > 0;
    switch (tile.key) {
        case 'late':
        case 'p1':
        case 'risk':
            return filled ? 'red bordered-red' : 'green bordered-green';
        case 'soon':
        case 'in_approval':
        case 'unassigned':
            return filled ? 'yellow bordered-orange' : 'bordered-green';
        case 'extended':
        case 'repeat':
            return filled ? 'yellow bordered-yellow' : 'bordered-green';
        case 'refund':
            return filled ? 'red bordered-red' : 'bordered-green';
        case 'closed':
            return 'green bordered-green';
        case 'open':
        case 'waiting_draft':
        case 'new_today':
            return 'bordered-blue';
        default:
            break;
    }
    if (tile.tone === 'bad') return filled ? 'red bordered-red' : 'green bordered-green';
    if (tile.tone === 'warn') return filled ? 'yellow bordered-yellow' : 'bordered-green';
    if (tile.tone === 'good') return 'green bordered-green';
    return 'bordered-blue';
}

/** Подпись под названием: цветная, только когда есть что подсвечивать. */
function subClass(tile: KpiTile): string {
    if (tile.value === 0) return '';
    if (tile.tone === 'bad') return 'highlight-red';
    if (tile.tone === 'warn') return 'highlight-orange';
    if (tile.tone === 'good') return 'highlight-green';
    return '';
}

interface Props {
    tiles: KpiTile[];
    openList: OpenList;
    toast: ShowToast;
}

export function KpiGrid({ tiles, openList, toast }: Props) {
    const activate = (tile: KpiTile): void => {
        if (tile.value <= 0) {
            toast('В этой выборке нет дел');
            return;
        }
        openList({ title: KPI_TITLES[tile.key] ?? tile.label, kpi: tile.key });
    };

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>, tile: KpiTile): void => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        activate(tile);
    };

    return (
        <div className="kpi-grid" id="kpiGrid">
            {tiles.map((tile) => {
                const hl = subClass(tile);
                return (
                    <div
                        key={tile.key}
                        className={`kpi-card clickable ${toneClass(tile)}`}
                        data-kpi={tile.key}
                        role="button"
                        tabIndex={0}
                        aria-label={`${tile.label}: ${tile.value}`}
                        onClick={() => activate(tile)}
                        onKeyDown={(event) => onKeyDown(event, tile)}
                    >
                        <div className="kpi-top">
                            <span className="kpi-value">{tile.value}</span>
                            <span className="kpi-icon">{KPI_ICONS[tile.key] ?? '📌'}</span>
                        </div>
                        <div className="kpi-label">{tile.label}</div>
                        <div className="kpi-sub">
                            {hl ? <span className={hl}>{tile.sub}</span> : tile.sub}
                            {tile.delta !== null && (
                                <>
                                    {' · '}
                                    <span className="change">
                                        <Delta delta={tile.delta} />
                                    </span>
                                </>
                            )}
                        </div>
                        <span className={tile.scope === 'period' ? 'scope-tag period' : 'scope-tag'}>
                            {tile.scope === 'period' ? 'за период' : 'на сейчас'}
                        </span>
                        {tile.value > 0 && <div className="kpi-more">Показать список →</div>}
                    </div>
                );
            })}
        </div>
    );
}
